import { CalendarClock } from 'lucide-react';
import type { ProbationReviewEntry } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useProbationReviews } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { PersonRow } from '../person-row';
import { ExpandableList } from '../expandable-list';
import { Chip } from '../chip';
import { formatDay } from '../widget-format';

function sublineFor(row: ProbationReviewEntry): string {
  if (row.kind === 'compensation-review') return 'Compensation review due';
  if (row.daysUntil === 0) return 'Probation ends today';
  return `Probation ends in ${row.daysUntil} day${row.daysUntil === 1 ? '' : 's'}`;
}

/**
 * `probationReviews` widget — combined "due soon" list: probation periods
 * ending soon + compensation reviews due soon, soonest first (order from the API).
 * Each row links to the profile; capped with the shared `ExpandableList`.
 */
export function ProbationReviewsWidget() {
  const { data, isLoading, isError } = useProbationReviews();
  const rows = data ?? [];

  return (
    <WidgetCard
      title="Probation & Reviews Due Soon"
      icon={CalendarClock}
      caption="Probation ending ≤ 10 days · compensation review due ≤ 30 days."
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-5 w-12" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load upcoming probations and reviews.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">Nothing due soon.</p>
      ) : (
        <ExpandableList
          items={rows}
          getKey={(r) => `${r.kind}-${r.id}`}
          renderItem={(r: ProbationReviewEntry) => (
            <PersonRow
              employee={r}
              subline={sublineFor(r)}
              trailing={<Chip color={r.daysUntil <= 7 ? 'amber' : 'gray'}>{formatDay(r.date)}</Chip>}
            />
          )}
        />
      )}
    </WidgetCard>
  );
}
